import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, KeyRound, ShieldAlert } from "lucide-react";
import { api, errMsg } from "../../lib/api";

const ROLE_LABELS = { org_admin: "Administrators", manager: "Managers", user: "Users", consultant: "Consultants", read_only: "Read-only users" };

function AddonCard({ st, reload }) {
  const subscribe = async () => {
    try {
      const { data } = await api.post("/ai/addon/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel the QORDEL AI add-on at the end of this billing period? Your IMS data is not affected.")) return;
    try {
      await api.post("/ai/addon/cancel");
      toast.success("Add-on will end at the end of the current period");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const a = st.addon;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ai-addon-card">
      <h3 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-600" />QORDEL AI Integration add-on</h3>
      <p className="text-sm text-slate-600 mt-1">An optional assistant for audits, NCRs, corrective actions, risks, document control and management reviews, powered by your own Anthropic Claude account. QORDEL 360 works fully without it.</p>
      <p className="mt-3 text-sm"><b data-testid="ai-addon-price">A${Number(a.price_aud).toFixed(2)} per month</b> for the integration, billed by QORDEL 360.</p>
      <p className="mt-1 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="ai-anthropic-notice">Claude API usage is charged separately by Anthropic to your own Anthropic account. QORDEL does not buy, resell or subsidise Claude usage.</p>
      <div className="mt-4 flex items-center gap-3">
        <span className={`text-sm font-semibold ${a.active ? "text-emerald-700" : "text-slate-500"}`} data-testid="ai-addon-status">{a.active ? (a.complimentary ? "Active (complimentary)" : `Active${a.cancel_at_period_end ? " — ends at period end" : ""}`) : "Not subscribed"}</span>
        {!a.active && a.available && <button onClick={subscribe} className="bg-sky-600 text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="ai-addon-subscribe-btn">Subscribe to add-on</button>}
        {a.active && a.subscription_id && !a.cancel_at_period_end && <button onClick={cancel} className="text-sm font-semibold text-red-700" data-testid="ai-addon-cancel-btn">Cancel add-on</button>}
      </div>
    </div>
  );
}

function KeyCard({ st, reload }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    reload();
  };
  const connect = () => run(async () => {
    await api.put("/ai/key", { api_key: key });
    setKey("");
    toast.success("Anthropic key verified and saved securely");
  });
  const test = () => run(async () => {
    const { data } = await api.post("/ai/key/test");
    data.ok ? toast.success(data.message) : toast.error(data.message);
  });
  const disconnect = () => window.confirm("Disconnect the Anthropic key? QORDEL AI will be turned off. Your IMS data and past conversations are kept.") && run(async () => {
    await api.delete("/ai/key");
    toast.success("Key disconnected");
  });
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ai-key-card">
      <h3 className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-sky-600" />Anthropic API key</h3>
      <p className="text-sm text-slate-600 mt-1">Create a key in the <a href="https://platform.claude.com/settings/keys" target="_blank" rel="noreferrer" className="text-sky-700 underline">Anthropic Console</a> and paste it here. It is verified with Anthropic, encrypted before storage, and never shown again or sent to anyone's browser.</p>
      <p className="mt-3 text-sm" data-testid="ai-key-status">{st.connected ? <>Connected: <span className="font-mono">{st.key_hint}</span>{st.last_tested_at && ` · last checked ${new Date(st.last_tested_at).toLocaleString("en-AU")}`}</> : "No key connected"}</p>
      {st.last_error && <p className="mt-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md p-2.5 flex gap-2" data-testid="ai-key-error"><ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />{st.last_error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk-ant-…" className="field flex-1 min-w-[14rem] font-mono" data-testid="ai-key-input" />
        <button disabled={busy || key.length < 20} onClick={connect} className="bg-sky-600 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="ai-key-connect-btn">{st.connected ? "Replace key" : "Verify & connect"}</button>
        {st.connected && <button disabled={busy} onClick={test} className="border border-slate-300 text-sm font-semibold px-4 py-2 rounded-md" data-testid="ai-key-test-btn">Test connection</button>}
        {st.connected && <button disabled={busy} onClick={disconnect} className="text-sm font-semibold text-red-700 px-2" data-testid="ai-key-disconnect-btn">Disconnect</button>}
      </div>
    </div>
  );
}

function ControlsCard({ st, reload }) {
  const [f, setF] = useState({ enabled: st.enabled, model: st.model, roles: st.roles, monthly_token_cap: st.monthly_token_cap });
  const toggleRole = (r) => setF({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] });
  const save = async () => {
    try {
      await api.put("/ai/settings", f);
      toast.success("AI settings saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="ai-controls-card">
      <h3 className="font-semibold">Access &amp; limits</h3>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="ai-enabled-toggle" />Turn on QORDEL AI for this organisation</label>
      <label className="block text-sm">Claude model
        <select value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} className="field mt-1 max-w-xs" data-testid="ai-model-select">{st.models.map((m) => <option key={m} value={m}>{m}</option>)}</select>
      </label>
      <div className="text-sm"><p>Who can use QORDEL AI</p><div className="mt-1 flex flex-wrap gap-3">{Object.entries(ROLE_LABELS).map(([r, l]) => <label key={r} className="flex items-center gap-1.5"><input type="checkbox" checked={f.roles.includes(r)} onChange={() => toggleRole(r)} data-testid={`ai-role-${r}`} />{l}</label>)}</div></div>
      <label className="block text-sm">Monthly token limit (0 = no QORDEL limit; Anthropic limits still apply)
        <input type="number" min={0} value={f.monthly_token_cap} onChange={(e) => setF({ ...f, monthly_token_cap: Number(e.target.value) })} className="field mt-1 max-w-xs" data-testid="ai-token-cap-input" />
      </label>
      <p className="text-sm text-slate-600" data-testid="ai-usage-summary">This month: {st.usage.requests} questions · {st.usage.input_tokens.toLocaleString()} input + {st.usage.output_tokens.toLocaleString()} output tokens (billed to you by Anthropic).</p>
      <p className="text-xs text-slate-500">Conversations are private to each user and deleted automatically after 12 months. QORDEL AI only reads your organisation's records; it cannot approve documents, close NCRs, certify compliance or change any record.</p>
      <button onClick={save} className="bg-sky-600 text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="ai-settings-save-btn">Save settings</button>
    </div>
  );
}

export function AiIntegration() {
  const [st, setSt] = useState(null);
  const load = () => api.get("/ai/settings").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("ai_session");
    if (sid) {
      api.post("/ai/addon/confirm", { session_id: sid }).then((r) => r.data.active && toast.success("QORDEL AI add-on activated")).catch(() => {}).finally(load);
    } else {
      load();
    }
  }, []);
  if (!st) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="ai-integration-settings">
      <AddonCard st={st} reload={load} />
      <KeyCard st={st} reload={load} />
      {st.connected && <ControlsCard key={`${st.model}-${st.enabled}`} st={st} reload={load} />}
    </div>
  );
}
